import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Plane, 
  Map, 
  Settings,
  CreditCard,
  Globe,
  PlusCircle,
  BarChart3,
  X,
  ChevronRight,
  DollarSign,
  TreePine
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const navItems = [
  { 
    path: '/dashboard', 
    label: 'Dashboard',
    icon: LayoutDashboard 
  },
  { 
    path: '/suppliers', 
    label: 'Suppliers',
    icon: Users 
  },
  { 
    path: '/new-case', 
    label: 'Add Worker',
    icon: PlusCircle 
  },
  { 
    path: '/visa-center', 
    label: 'Visa Center',
    icon: FileText 
  },
  { 
    path: '/flight-scanner', 
    label: 'Flight Scanner',
    icon: Plane 
  },
  { 
    path: '/arrivals-map', 
    label: 'Arrivals Map',
    icon: Map 
  },
  { 
    path: '/worker-tree', 
    label: 'Worker Tree',
    labelAr: 'شجرة العاملات',
    icon: TreePine 
  },
  { 
    path: '/supplier-reports', 
    label: 'Supplier Reports',
    icon: BarChart3 
  },
  { 
    path: '/transactions', 
    label: 'Transactions',
    icon: CreditCard 
  },
  { 
    path: '/advance-payments', 
    label: 'Advance Payments',
    icon: DollarSign 
  },
  { 
    path: '/overseas-workers', 
    label: 'Overseas Workers',
    icon: Globe 
  },
  { 
    path: '/settings', 
    label: 'Settings',
    icon: Settings 
  },
];

// Nav Item Component
const NavItem = ({ item, onClick, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.05 }}
  >
    <NavLink
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.path.replace('/', '')}`}
      className={({ isActive }) => cn(
        "flex items-center gap-3 px-4 py-3 rounded-xl",
        "transition-all duration-300 group relative overflow-hidden",
        "hover:bg-white/5",
        isActive 
          ? "bg-gradient-to-r from-orange-500/20 to-orange-600/10 border-l-2 border-orange-500 text-orange-400" 
          : "text-slate-400 hover:text-white border-l-2 border-transparent"
      )}
    >
      {({ isActive }) => (
        <>
          {/* Background glow for active state */}
          {isActive && (
            <motion.div
              layoutId="activeNavBg"
              className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-transparent"
              initial={false}
              transition={{ type: "spring", duration: 0.5 }}
            />
          )}
          
          {/* Icon */}
          <div className={cn(
            "relative z-10 p-1.5 rounded-lg transition-all duration-300",
            isActive 
              ? "bg-orange-500/20 text-orange-400" 
              : "text-slate-500 group-hover:text-orange-400 group-hover:bg-orange-500/10"
          )}>
            <item.icon className="h-4 w-4" />
          </div>
          
          {/* Label */}
          <span className={cn(
            "relative z-10 text-sm font-medium transition-all duration-300",
            isActive ? "text-orange-400" : "group-hover:text-white"
          )}>
            {item.label}
          </span>
          
          {/* Arrow indicator for active */}
          {isActive && (
            <ChevronRight className="h-4 w-4 ml-auto text-orange-500" />
          )}
        </>
      )}
    </NavLink>
  </motion.div>
);

const Sidebar = ({ isOpen, onClose }) => {
  return (
    <>
      {/* Overlay for mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-50",
        "w-64 bg-[#0A0F1A]/95 backdrop-blur-xl",
        "border-r border-white/5",
        "transform transition-transform duration-300 ease-in-out",
        "lg:transform-none flex flex-col",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Close button for mobile */}
        <div className="flex justify-end p-2 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/10 rounded-xl"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Logo */}
        <div className="p-5 border-b border-white/5">
          <motion.div 
            className="flex items-center gap-3"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Logo with glow */}
            <div className="relative">
              <div className="absolute inset-0 bg-orange-500/30 rounded-xl blur-lg" />
              <div className="relative h-11 w-11 rounded-xl bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center shadow-lg shadow-orange-500/25 border border-orange-400/30">
                <span className="text-xl font-black text-white">T</span>
              </div>
            </div>
            <div>
              <h2 className="font-bold text-sm text-white tracking-wide">TADABER</h2>
              <p className="text-[10px] text-slate-500 tracking-widest">WORKFORCE</p>
            </div>
          </motion.div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {navItems.map((item, index) => (
            <NavItem 
              key={item.path} 
              item={item} 
              onClick={onClose}
              index={index}
            />
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center justify-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <p className="text-[10px] text-slate-600">
              v3.7 - Tadaber Workforce
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
